/**
 * Lenient JSON parser — handles JSONC (JSON with Comments).
 *
 * Strips single-line comments (//), block comments, and trailing commas
 * before parsing with standard JSON.parse().
 *
 * Falls back to strict JSON.parse() if stripping fails.
 */

/**
 * Strip JSONC features (comments, trailing commas) from a string.
 * Returns clean JSON suitable for JSON.parse().
 */
function stripJsonComments(input: string): string {
  let result = '';
  let i = 0;
  let inString = false;
  let stringChar = '';

  while (i < input.length) {
    const ch = input[i];
    const next = input[i + 1];

    // Handle string literals (don't strip inside strings)
    if (inString) {
      result += ch;
      if (ch === '\\') {
        // Escaped character — copy next char too
        i++;
        if (i < input.length) result += input[i];
      } else if (ch === stringChar) {
        inString = false;
      }
      i++;
      continue;
    }

    // Start of string
    if (ch === '"' || ch === "'") {
      inString = true;
      stringChar = ch;
      result += ch;
      i++;
      continue;
    }

    // Single-line comment
    if (ch === '/' && next === '/') {
      while (i < input.length && input[i] !== '\n') i++;
      continue;
    }

    // Block comment
    if (ch === '/' && next === '*') {
      i += 2;
      while (i < input.length - 1 && !(input[i] === '*' && input[i + 1] === '/')) i++;
      i += 2; // skip closing */
      continue;
    }

    result += ch;
    i++;
  }

  // Remove trailing commas before } or ]
  return result.replace(/,\s*([\]}])/g, '$1');
}

/**
 * Parse JSON leniently — supports comments and trailing commas.
 * Tries strict parse first (fast path), falls back to stripping.
 */
export function parseJsonLenient(input: string): unknown {
  // Fast path: try strict parse first
  try {
    return JSON.parse(input);
  } catch {
    // Slow path: strip comments and trailing commas
    const cleaned = stripJsonComments(input);
    return JSON.parse(cleaned); // Let this throw if still invalid
  }
}
